import React from 'react';
import { get, isEmpty, isUndefined, cloneDeep, map, merge, find, filter, forEach, isBoolean, castArray, head } from 'lodash';
import { withRouter } from 'react-router-dom';

// local dependencies
import { reactivateEpostInsuranceInformationStep as ReactivateEpostInsuranceInformationStepView } from 'ui/views/widgets/reactivateEpostInsuranceInformationStep';
import { ReactivateEpostInsuranceInformationStepService } from 'services/reactivateEpostInsuranceInformationStep';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, SIZES, GLOBAL_LABELS, BLANK_VALUE_SYMBOL } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import * as confirmOverlay from 'utils/confirmOverlay';
import { buildReactivateRequestPayload, findEligibleCoverage, isEligibleCoverage } from 'utils/epostCoverageSelection';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ReactivateEpostInsuranceInformationStep } from 'ui/containers/ReactivateEpostInsuranceInformationStep';
 *
 * // render
 * <ReactivateEpostInsuranceInformationStep propName='propValue'/>
 */
class _ReactivateEpostInsuranceInformationStep extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			isCallDispositionVisible: false,
            callReasonValidationState: false,
            isFetchingAddress: false,
            isFetchingCommunicationList: false,
            isBasicInfoFormValid: true
		};

		// create service instance
		this.reactivateEpostInsuranceInformationStepService = new ReactivateEpostInsuranceInformationStepService();

		// start component data fetch
		this.fetchComponentData();

		// bind
        this.__bind();
    }

	/*********************************************/

	__bind() {
		this.handleNavActions = this.handleNavActions.bind( this );
        this.updateBasicInfo = this.updateBasicInfo.bind( this );
        this.handleDrugPreferenceSwitch = this.handleDrugPreferenceSwitch.bind( this );
        this.handleAddressInfoUpdate = this.handleAddressInfoUpdate.bind( this );
        this.handleDeleteAddressAction = this.handleDeleteAddressAction.bind( this );
        this.handleSetAddressDefaultAction = this.handleSetAddressDefaultAction.bind( this );
        this.handleAddEditCommunication = this.handleAddEditCommunication.bind( this );
        this.handleCommunicationDefaultSelection = this.handleCommunicationDefaultSelection.bind( this );
        this.handleCommunicationDetailDelete = this.handleCommunicationDetailDelete.bind( this );
        this.submitStepData = this.submitStepData.bind( this );
        this.handleAddressActivate = this.handleAddressActivate.bind( this );
        this.handleAddressInactivate = this.handleAddressInactivate.bind( this );
        this.handleSwitchBillingAddressSameAsShipping = this.handleSwitchBillingAddressSameAsShipping.bind( this );
        this.handleSetAddressDefaultActionBasedOnSection = this.handleSetAddressDefaultActionBasedOnSection.bind( this );
        this.handleCoverageSelection = this.handleCoverageSelection.bind( this );
    }

    handleCoverageSelection( value ) {
        const selectedValue = head( castArray( value ) );
        const coverages = get( this.state, 'componentData.insurance.eligibleCoverages', [] ) || [];
        const selectedCoverage = findEligibleCoverage( coverages, selectedValue );

        if ( ! selectedCoverage ) {
            return;
        }

        const componentData = cloneDeep( this.state.componentData );
        componentData.insurance.currentInsurance = selectedCoverage;
        this.setState( { componentData } );
    }

    getRequestPayload() {
        const requestPayload = cloneDeep( this.props.requestPayload );
        const hasLiveCoverageOptions = get( this.state, 'componentData.insurance.eligibleCoverages.length', 0 ) > 0;
        const selectedCoverage = get( this.state, 'componentData.insurance.currentInsurance', {} ) || {};

        if ( hasLiveCoverageOptions && selectedCoverage.carrier && selectedCoverage.account && selectedCoverage.group ) {
            requestPayload.context = {
                ...( requestPayload.context || {} ),
                carrierId: selectedCoverage.carrier,
                accountId: selectedCoverage.account,
                groupId: selectedCoverage.group,
            };
        }

        return requestPayload;
    }

    /**
     * executed when user makes and update in the basic info form
     */
    updateBasicInfo( { name, dob, email, isValid } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.updateBasicInfo()' );

        if( ! isValid ) {
            return this.setState( { isBasicInfoFormValid: isValid } );
        }

        const data = get( this.state, 'componentData', {} );
        data.basicInformation.name = name;
        data.basicInformation.dob = dob;
        data.basicInformation.email = email;

        this.setState( {
            componentData: data,
            isBasicInfoFormValid: isValid
        } );
    }

    /**
     * switch drug prefernce action
     */
    handleDrugPreferenceSwitch( { value } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleDrugPreferenceSwitch()' );

        const data = get( this.state, 'componentData', {} );
        data.drugPreference.useGeneric = value;
        this.setState( {
            componentData: data
        } );
    }

    /******************************** address details functions **********************************/

     /**
     * add/edit opration handler
     */
    async handleAddressInfoUpdate( { type, address, id = null } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleAddressInfoUpdate()' );

        // get action
        const action = get( this.state, 'componentData.sections.shippingAddress.addAction', {} );

        // cancel ongoing request
        this.reactivateEpostInsuranceInformationStepService.cancel();
        
        // show page preloader
        pageLevelPreloader.show();

        const formData = {
            ...address,
            id: id,
        };

        if( ! isEmpty( id ) ) {
            const selectedAddress = find( this.state.componentData.shippingAddress.addresses, { id: id } );
            formData.isDefault = ! isUndefined( selectedAddress ) ? get( selectedAddress, 'flags.isDefault' ): null;
        }

        // get the request payload
        const requestPayload = this.getRequestPayload();
        const dataPayLoad =  merge( requestPayload,
            {
                data: formData,
                context: { operation: 'add' === type ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE }
            }
        );

         // set the address default
         const { status, data } = await this.reactivateEpostInsuranceInformationStepService.submitSelectedAddress( {
            url: get( action, 'config.submitUrl', null ),
            data: dataPayLoad
        } );

        // hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            this.setState( {
                isFetchingAddress: true
            }, () => {
                this.fetchComponentData();
            } );

        } else {
            
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * delete address action
     */
    async handleDeleteAddressAction( { address } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleDeleteAddressAction()' );

        const isDefault = get( address, 'flags.isDefault', false );

        // get action
        const action = find( this.state.componentData.sections.shippingAddress.modifyActions, { name: 'delete' } );

        if( ! isDefault ) {

            const addressOption = get( this.state, 'componentData.options.addressType', [] );
            const type = find( addressOption, { value: address.type } );

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( get( action, 'confirmation', {} ) ),
                iconName: 'close',
                confirmViewConfig: {
                    type: 'address',
                    address: {
                        type: isUndefined( type ) ? BLANK_VALUE_SYMBOL: getLabel( type.title ),
                        value: filter( [ address.line1, address.line2, address.city, address.state, address.zip ], Boolean ).join( ', ' )
                    }
                }
            } );

            if( shouldProceed ) {

                // cancel ongoing request
                this.reactivateEpostInsuranceInformationStepService.cancel();
                            
                // show page preloader
                pageLevelPreloader.show();

                // get the request payload
                const requestPayload = this.getRequestPayload();
                const dataPayLoad =  merge( requestPayload,
                    {
                        data: { id: address.id },
                        context: { operation: CRUD_OPERATION.DELETE }
                    }
                );

                // set the address default
                const { status, data } = await this.reactivateEpostInsuranceInformationStepService.deleteSavedAddress( {
                    url: get( action, 'url', null ),
                    data: dataPayLoad
                } );

                // hide preloader
                pageLevelPreloader.hide();

                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                    const componentData = get( this.state, 'componentData', {} );
                    const newAddress = filter( componentData.shippingAddress.addresses, _address => {
                        return _address.id !== address.id;
                    } );
                    componentData.shippingAddress.addresses = newAddress;
                    this.setState( { componentData: componentData } );

                } else {

                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
                    } else {
                        errorOverlay.open();
                    }
                }
            }

        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
     * delete address action
     */
    async handleAddressInactivate( { address } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleAddressInactivate()' );

        const isDefault = get( address, 'flags.isDefault', false );

        // get action
        const action = find( this.state.componentData.sections.shippingAddress.modifyActions, { name: 'inactivate' } );

        if( ! isDefault ) {

            const addressOption = get( this.state, 'componentData.options.addressType', [] );
            const type = find( addressOption, { value: address.type } );

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( get( action, 'confirmation', {} ) ),
                iconName: 'close',
                confirmViewConfig: {
                    type: 'address',
                    address: {
                        type: isUndefined( type ) ? BLANK_VALUE_SYMBOL: getLabel( type.title ),
                        value: filter( [ address.line1, address.line2, address.city, address.state, address.zip ], Boolean ).join( ', ' )
                    }
                }
            } );

            if( shouldProceed ) {

                // cancel ongoing request
                this.reactivateEpostInsuranceInformationStepService.cancel();
                            
                // show page preloader
                pageLevelPreloader.show();

                // get the request payload
                const requestPayload = this.getRequestPayload();
                const dataPayLoad =  merge( requestPayload,
                    {
                        data: {
                            id: address.id,
                            type: address.type,
                            line1: address.line1,
                            line2: address.line2,
                            city: address.city,
                            state: address.state,
                            zip: address.zip
                        }
                    }
                );

                // set the address default
                const { status, data } = await this.reactivateEpostInsuranceInformationStepService.inactivateSavedAddress( {
                    url: get( action, 'url', null ),
                    data: dataPayLoad
                } );

                // hide preloader
                pageLevelPreloader.hide();

                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                    // update Item
                    const componentData = cloneDeep( this.state.componentData );
                    const updatedItem = find( get( componentData, 'shippingAddress.addresses' ), { id: address.id  } );
           
                    updatedItem.flags.isActivated = false;
                    this.setState( { componentData: componentData } );

                } else {

                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
                    } else {
                        errorOverlay.open();
                    }
                }
            }

        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
     * delete address action
     */
    async handleAddressActivate( { address } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleAddressActivate()' );

        // get action
        const action = find( this.state.componentData.sections.shippingAddress.modifyActions, { name: 'activate' } );

        // cancel ongoing request
        this.reactivateEpostInsuranceInformationStepService.cancel();
                    
        // show page preloader
        pageLevelPreloader.show();

        // get the request payload
        const requestPayload = this.getRequestPayload();
        const dataPayLoad =  merge( requestPayload,
            {
                data: {
                    id: address.id,
                    type: address.type,
                    line1: address.line1,
                    line2: address.line2,
                    city: address.city,
                    state: address.state,
                    zip: address.zip
                }
            }
        );

        // set the address default
        const { status, data } = await this.reactivateEpostInsuranceInformationStepService.activateSavedAddress( {
            url: get( action, 'url', null ),
            data: dataPayLoad
        } );

        // hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedItem = find( get( componentData, 'shippingAddress.addresses' ), { id: address.id  } );
           
            updatedItem.flags.isActivated = true;
            this.setState( { componentData: componentData } );

        } else {

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * address default selection
     */
    async handleSetAddressDefaultAction( { address } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleSetAddressDefaultAction()' );

        // cancel ongoing request
        this.reactivateEpostInsuranceInformationStepService.cancel();

        //show preloader
        pageLevelPreloader.show();

        const url = find( this.state.componentData.sections.shippingAddress.modifyActions, { name: 'setDefault' } )[ 'url' ];

        // get the request payload
        const requestPayload = this.getRequestPayload();
        const dataPayLoad =  merge(
            requestPayload,
            { data: { id: address.id } }
        );

        // set the address default
        const { status, data } = await this.reactivateEpostInsuranceInformationStepService.setDefaultAddress( {
            url,
            data: dataPayLoad
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedList = map( componentData.shippingAddress.addresses, item => {
                item.flags.isDefault = address.id === item.id ? true: false;
                return item;
            } );
            componentData.shippingAddress.addresses = updatedList;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                isFetching: true
            }, () => {

                // Fetch component data again to sync
                // inactivate flags from impl: 40683
                this.fetchComponentData();
                
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );

        } else {
            
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }
    
    /******************************** communication details functions **********************************/

    /**
     * add/edit communication operation handler
     */
    async handleAddEditCommunication( contact ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleAddEditCommunication()' );

        // get action
        const action = get( this.state, 'componentData.sections.contactInformation.addAction', {} );

        // cancel ongoing request
        this.reactivateEpostInsuranceInformationStepService.cancel();
        
        // show page preloader
        pageLevelPreloader.show();

        const formData = cloneDeep( contact );

        if( ! isEmpty( formData.id ) ) {
            const selectedContact = find( this.state.componentData.contactInformation.contacts, { id: formData.id } );
            formData.isDefault = ! isUndefined( selectedContact ) ? get( selectedContact, 'flags.isDefault' ): null;
        }

        // get the request payload
        const requestPayload = this.getRequestPayload();
        const dataPayLoad =  merge( requestPayload,
            {
                data: formData,
                context: { operation: isEmpty( formData.id ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE }
            }
        );

         // set the address default
         const { status, data } = await this.reactivateEpostInsuranceInformationStepService.addUpdatePhoneNumber( {
            url: get( action, 'config.submitUrl', null ),
            data: dataPayLoad
        } );

        // hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            this.setState( {
                isFetchingCommunicationList: true
            }, () => {
                this.fetchComponentData();
            } );

        } else {
            
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * default selection action handler
     */
    async handleCommunicationDefaultSelection( { item } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleCommunicationDefaultSelection()' );

        // cancel ongoing request
        this.reactivateEpostInsuranceInformationStepService.cancel();

        //show preloader
        pageLevelPreloader.show();

        const url = find( this.state.componentData.sections.contactInformation.modifyActions, { name: 'setDefault' } )[ 'url' ];

        // get the request payload
        const requestPayload = this.getRequestPayload();
        const dataPayLoad =  merge(
            requestPayload,
            { data: { id: item.id } }
        );

        // set the address default
        const { status, data } = await this.reactivateEpostInsuranceInformationStepService.setDefaultAddress( {
            url,
            data: dataPayLoad
        } );

        //show preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            const componentData = get( this.state, 'componentData', {} );
            const contactList = get( componentData, 'contactInformation.contacts' );

            forEach( contactList, contact => {
                contact.flags.isDefault = item.id === contact.id;
            } );
            
            componentData.contactInformation.contacts = contactList;
            this.setState( { componentData: componentData } );

        } else {

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * delete communication action handler
     */
    async handleCommunicationDetailDelete( { item } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleCommunicationDetailDelete()' );

        const isDefault = get( item, 'flags.isDefault', false );

        const action = find( this.state.componentData.sections.contactInformation.modifyActions, { name: 'delete' } );

        if( ! isDefault ) {

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( get( action, 'confirmation', {} ) ),
                iconName: 'close'
            } );

            if( shouldProceed ) {

                // cancel ongoing request
                this.reactivateEpostInsuranceInformationStepService.cancel();

                //show preloader
                pageLevelPreloader.show();

                // get the request payload
                const requestPayload = this.getRequestPayload();
                const dataPayLoad =  merge(
                    requestPayload,
                    {
                        data: { id: item.id },

                        // added operation type because of production defect
                        context: { operation: CRUD_OPERATION.DELETE }
                    }
                );

                // set the address default
                const { status, data } = await this.reactivateEpostInsuranceInformationStepService.deleteCommunicationDelete( {
                    url: action.url,
                    data: dataPayLoad
                } );

                //show preloader
                pageLevelPreloader.hide();

                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                    const componentData = get( this.state, 'componentData', {} );
                    const contactList = get( componentData, 'contactInformation.contacts' );
                    const newConactList = filter( contactList, _contact => {
                        return _contact.id !== item.id;
                    } );
                    
                    componentData.contactInformation.contacts = newConactList;
                    this.setState( { componentData: componentData } );

                } else {
                    
                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
                    } else {
                        errorOverlay.open();
                    }
                }
            }

        } else {
            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /************************************************************ */
	
	/**
     * submit data
     */
    async submitStepData( { data } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.submitStepData()' );

        const action = find( this.state.componentData.navActions, { name: 'next' } );

        // cancel
        this.reactivateEpostInsuranceInformationStepService.cancel();

        // show preloader
        pageLevelPreloader.show();

        // get the request payload
        const requestPayload = this.getRequestPayload();
        const hasLiveCoverageOptions = get( this.state, 'componentData.insurance.eligibleCoverages.length', 0 ) > 0;
        const dataPayLoad = buildReactivateRequestPayload( requestPayload, data, hasLiveCoverageOptions );

        const { status, _data } = await this.reactivateEpostInsuranceInformationStepService.sumbitStepdata( {
            url: action.url,
            data: dataPayLoad
        } );

        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.props.moveForward();
        } else {
			if ( ! isEmpty( _data.description ) ) {
				errorOverlay.open( { message: _data.description, size: SIZES.SMALL } );
			} else {
				errorOverlay.open();
			}
        }
    }

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'ReactivateEpostInsuranceInformationStep.fetchComponentData()' );

		// cancel ongoing request
		this.reactivateEpostInsuranceInformationStepService.cancel();

		// fetch component data
		const {
			status,
			data,
		} = await this.reactivateEpostInsuranceInformationStepService.fetchComponentData( {
			url: this.props.widget.url,
			data: this.props.requestPayload
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
                isFetching: false,
                isFetchingAddress: false,
                isFetchingCommunicationList: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			} );
		}
    }

    /**
     * handle nav actions
     */
    async handleNavActions( { action } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleNavActions()' );

        if( 'cancel' === action.name ) {
            if ( get( action, 'url' ) && get( this.props, 'requestPayload.context.reactivationFlowId' ) ) {
                const cancelStatus = await this.reactivateEpostInsuranceInformationStepService.cancelReactivationFlow( {
                    url: get( action, 'url', null ),
                    data: this.props.requestPayload,
                } );
                if ( SERVICE_RESPONSE_STATUS.SUCCESS !== cancelStatus ) {
                    errorOverlay.open();
                    return;
                }
            }
            this.props.history.goBack();
        }

        if( 'next' === action.name ) {
            const liveCoverages = get( this.state, 'componentData.insurance.eligibleCoverages', [] ) || [];
            const chosenCoverage = get( this.state, 'componentData.insurance.currentInsurance', {} ) || {};
            if ( liveCoverages.length > 0 && ! isEligibleCoverage( liveCoverages, chosenCoverage ) ) {
                errorOverlay.open();
                return;
            }
            const shippingAddress =  get( this.state, 'componentData.shippingAddress.addresses', [] )
            const defaultShippingAddress = shippingAddress.filter(obj=> obj.flags && obj.flags.isDefault && obj.flags.isActivated )
            const data = {
                basicInformation: get( this.state, 'componentData.basicInformation', {} ),
                useGeneric: get( this.state, 'componentData.drugPreference.useGeneric' ),
                insurance: get( this.state, 'componentData.insurance.currentInsurance', {} ),
                billingAddress: get( this.state, 'componentData.billingRequestData', {addresses: defaultShippingAddress} )
            };
            this.submitStepData( { data } );
        }
    }

    /**
    * switch billing address same as shippping address
    */
     handleSwitchBillingAddressSameAsShipping( { value, isShippingUpdate = false } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleSwitchBillingAddressSameAsShipping()' );

        const data = get( this.state, 'componentData.billingAddress', {} );

        // If same value comes from view return from here
        if ( ( data.sameAsShipping === value ) && !isShippingUpdate ) {
            return;
        }

        const billingRequestData = {};

        data.sameAsShipping = value;
        billingRequestData.sameAsShipping = value;

        const _shippingAddresses = cloneDeep( get( this.state, 'componentData.shippingAddress.addresses', [] ) );

        let enableSelection = false;

        if ( value ) {

             data.addresses = _shippingAddresses.filter( ( addressItem ) => {

                // If isDefault true returns it
                if ( get( addressItem, 'flags.isDefault', false ) ) {
                    return addressItem;
                }
             } );

             billingRequestData.addresses = [ ...data.addresses ];


        } else {

            data.addresses = _shippingAddresses.map( ( addressItem ) => {

                // set all action flags to false
                const _flags = get( addressItem, 'actionsVisibility', {} );

                // Mutated object on purpose here
                for ( const flag in _flags ) {

                    _flags [ flag ] = false;
                }

                addressItem.selected = false;

                return addressItem;
             } );

             enableSelection = true;

             billingRequestData.addresses = [];
        }

        this.setState( {
            componentData: {
                ...this.state.componentData,
                billingAddress: data,
                billingRequestData: billingRequestData,
                enableSelection
            }
        } );

    }

    /**
    * make the address default
    */
     handleSetAddressDefaultActionBasedOnSection( { address, sectionName = null } ) {
        log.debug( 'ReactivateEpostInsuranceInformationStep.handleSetAddressDefaultActionBasedOnSection()' );

        const data = get( this.state, 'componentData', {} );
        const addressList = get( data, `${sectionName}.addresses` );

        forEach( addressList, _address => {

            _address.flags.isDefault = address.id === _address.id;
            _address.selected = _address.id === address.id ? true: false;

            if ( get( _address, 'flags.isDefault', false ) ) {

                data[ 'billingRequestData' ].addresses = [ { ..._address } ];
            }
        } );
        
        data[ sectionName ].addresses = addressList;
        
        this.setState( { componentData: data } );
    }
	
    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.reactivateEpostInsuranceInformationStepService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {

        // const basicInformation = get( this.state, 'componentData.basicInformation', {} );

		// const invalidInfo  = pickBy( basicInformation, ( _value = {} ) => ( isEmpty( _value ) ) );
        const contacts = get( this.state, 'componentData.contactInformation.contacts', [] );
        
		// const addresses = get( this.state, 'componentData.shippingAddress.addresses', [] );
		const drugPreference =  get( this.state, 'componentData.drugPreference.useGeneric', undefined );
		let canMoveForward = false;
        const billingAddresses = get( this.state, 'componentData.billingRequestData.addresses', [] );
        const eligibleCoverages = get( this.state, 'componentData.insurance.eligibleCoverages', [] ) || [];
        const selectedCoverage = get( this.state, 'componentData.insurance.currentInsurance' ) || {};

        const selectedCoverageIsEligible = isEmpty( eligibleCoverages ) || isEligibleCoverage( eligibleCoverages, selectedCoverage );
        if( ! isEmpty( contacts ) && selectedCoverageIsEligible && this.state.isBasicInfoFormValid  ) {
			canMoveForward = true;
        }
                
        return (
            <ReactivateEpostInsuranceInformationStepView
                isFetching={ this.state.isFetching }
                networkStatus={this.state.networkStatus }
                {...get( this.state, 'componentData', {} ) }
                onNavActionClick={ this.handleNavActions }

                onBasicInfoUpdate = { this.updateBasicInfo }
                onSwitchDrugPrefernce={ this.handleDrugPreferenceSwitch }

                onAddEditAddressClick={ this.handleAddressInfoUpdate }
                onAddressDefaultSelection={ this.handleSetAddressDefaultAction }
                onAddressDelete={ this.handleDeleteAddressAction }

                // Billing Address handlers
                onSwitchBillingAddressSameAsShipping= { this.handleSwitchBillingAddressSameAsShipping }
                onSetAddressDefaultActionBasedOnSection= { this.handleSetAddressDefaultActionBasedOnSection }

                onAddEditCommunicationDetail={ this.handleAddEditCommunication }
                onCommunicationDefaultSelection={ this.handleCommunicationDefaultSelection }
                onCommunicationDelete={ this.handleCommunicationDetailDelete }

                isFetchingAddress={ this.state.isFetchingAddress }
                isFetchingCommunicationList={ this.state.isFetchingCommunicationList }

                canMoveForward={ canMoveForward }

                onAddressInactivate = { this.handleAddressInactivate }
                onAddressActivate = { this.handleAddressActivate }
                onCoverageSelection = { this.handleCoverageSelection }
			/>
        );
    }
}

// set display name
_ReactivateEpostInsuranceInformationStep.displayName = 'ReactivateEpostInsuranceInformationStep';

// set default props
_ReactivateEpostInsuranceInformationStep.defaultProps = {};

// prop types
_ReactivateEpostInsuranceInformationStep.propTypes = {};

export const ReactivateEpostInsuranceInformationStep = withRouter( _ReactivateEpostInsuranceInformationStep );
